<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import { fixedTimesSummary } from '@/domain/reservations/reservation-shifts';
  import type { IReservationArrivalTime } from '@/interfaces/IReservationSettings';
  import { shortWeekdaysMonFirst } from '@/utils/intl-weekdays.utils';
  import { guideWeekdaysLabel } from './reservation-guide-copy';

  /**
   * One shift (stored or proposed) on the Turnos step: name, days in words,
   * hours, the arrival times it offers, seven weekday dots, «Cambiar» /
   * «Quitar» and, for a seeded «Turno N», the rename chips.
   */
  const props = defineProps<{
    name: string;
    days: number[];
    from: string;
    to: string;
    /** Minutes between grid arrivals. */
    slotIntervalMin: number;
    /** Fixed arrival times replacing the grid; absent or null = grid. */
    arrivalTimes?: IReservationArrivalTime[] | null;
    /** Offers the daypart names (stored shifts still called «Turno N»). */
    renameable?: boolean;
    /** A rename is in flight for this row. */
    renaming?: boolean;
    disabled?: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'change'): void;
    (e: 'remove'): void;
    (e: 'rename', name: string): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const WEEKDAYS_MON_FIRST: readonly number[] = [1, 2, 3, 4, 5, 6, 0];
  const RENAME_KEYS = [
    'renameBreakfast',
    'renameLunch',
    'renameDinner',
    'renameAllDay',
  ] as const;

  const locale = computed(() => proxy.$i18n.locale);
  const from = computed(() => props.from.slice(0, 5));
  const to = computed(() => props.to.slice(0, 5));
  const daysLabel = computed(() =>
    guideWeekdaysLabel(props.days, locale.value, tr)
  );
  const hoursLabel = computed(() =>
    tr('reservations.guide.fromTo', { from: from.value, to: to.value })
  );
  const nextDay = computed(() => to.value < from.value);
  const timesLabel = computed(() => {
    if (props.arrivalTimes) {
      const summary = fixedTimesSummary(props.arrivalTimes);
      const more = summary.hidden
        ? ` ${tr('reservations.preview.more', { count: summary.hidden })}`
        : '';
      return tr('reservations.shifts.fixedTimes', {
        times: `${summary.shown}${more}`,
      });
    }
    return tr('reservations.shifts.everyMinutes', {
      minutes: props.slotIntervalMin,
    });
  });
  const weekdayNames = computed(() =>
    shortWeekdaysMonFirst(locale.value).map((name) => name.replace(/\.$/, ''))
  );
  const renameOptions = computed(() =>
    RENAME_KEYS.map((key) => tr(`reservations.guide.hours.${key}`))
  );
  const busy = computed(() => !!props.disabled || !!props.renaming);
</script>

<template>
  <article class="reservations-guide-shift-row">
    <div class="row-body">
      <div class="row-details">
        <h3 class="shift-name">{{ name }}</h3>
        <p class="shift-days">{{ daysLabel }}</p>
        <p class="shift-hours">
          <span>{{ hoursLabel }}</span>
          <span v-if="nextDay" class="next-day">{{
            $t('reservations.guide.hours.nextDay')
          }}</span>
        </p>
        <p class="shift-times">{{ timesLabel }}</p>
        <div class="weekday-dots" aria-hidden="true">
          <span
            v-for="(day, index) in WEEKDAYS_MON_FIRST"
            :key="day"
            class="weekday-dot"
            :class="{ active: days.includes(day) }"
            :title="weekdayNames[index]"
          />
        </div>
      </div>
      <div class="row-actions">
        <PosUiButton
          color="neutral"
          variant="ghost"
          :aria-label="`${$t('reservations.guide.change')} · ${name}`"
          :disabled="busy"
          @click="emit('change')"
          >{{ $t('reservations.guide.change') }}</PosUiButton
        >
        <PosUiButton
          class="remove-button"
          color="neutral"
          variant="ghost"
          :aria-label="`${$t('reservations.guide.remove')} · ${name}`"
          :disabled="busy"
          @click="emit('remove')"
          >{{ $t('reservations.guide.remove') }}</PosUiButton
        >
      </div>
    </div>
    <div
      v-if="renameable"
      class="rename-chips"
      role="group"
      :aria-label="$t('reservations.shifts.form.name')"
    >
      <span class="rename-label">{{
        $t('reservations.guide.hours.renameLabel')
      }}</span>
      <PosUiChip
        v-for="option in renameOptions"
        :key="option"
        :disabled="busy"
        @click="emit('rename', option)"
        >{{ option }}</PosUiChip
      >
    </div>
  </article>
</template>

<style scoped lang="scss">
  .reservations-guide-shift-row {
    @apply tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-4;
    p {
      @apply tw-m-0;
    }
    .row-body {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3;
    }
    .row-details {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1;
    }
    .shift-name {
      @apply tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .shift-days {
      @apply tw-text-sm tw-text-neutral-700;
    }
    .shift-hours {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-sm tw-tabular-nums tw-text-neutral-700;
      .next-day {
        @apply tw-text-xs tw-text-neutral-500;
      }
    }
    .shift-times {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .weekday-dots {
      @apply tw-mt-1 tw-flex tw-gap-1.5;
    }
    .weekday-dot {
      @apply tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-200;
      &.active {
        @apply tw-bg-neutral-800;
      }
    }
    // Wraps under the label on phones; two ghost buttons side by side otherwise.
    .row-actions {
      @apply tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-1 sm:tw-flex-row sm:tw-items-center;
    }
    // Muted «Quitar»: restated with the button's own classes to beat its variant rule.
    .remove-button.pos-ui-button.color-neutral.variant-ghost {
      @apply tw-text-neutral-500;
    }
    .rename-chips {
      .rename-label {
        @apply tw-text-xs tw-text-neutral-500;
      }
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
  }
</style>
